import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { Star } from 'lucide-react';
import { reviewAPI } from '../../services/api';
import Spacing from '../Spacing';
import SectionHeading from '../SectionHeading';
import AnimatedSection from '../AnimatedSection';
import { pageTitle } from '../../helpers/PageTitle';

export default function SubmitReviewPage() {
  pageTitle('Submit Review | Ayumedix Wellness');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [reviewerName, setReviewerName] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('Invalid review link. Token missing.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      toast.error('Invalid review link. Token missing.');
      return;
    }

    if (!reviewerName.trim() || reviewerName.length < 2) {
      toast.error('Please enter your name');
      return;
    }

    if (rating === 0) {
      toast.error('Please select a star rating');
      return;
    }

    if (!reviewText.trim() || reviewText.length < 10) {
      toast.error('Please write at least 10 characters for your review');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await reviewAPI.submitReview(token, {
        token,
        rating,
        comment: reviewText,
        reviewer_name: reviewerName
      });

      if (response.data && response.data.status === 201) {
        setIsSuccess(true);
        toast.success('Review submitted successfully! Thank you for your feedback.');
        setTimeout(() => {
          navigate('/');
        }, 5000);
      } else {
        toast.error(response.data?.message || 'Failed to submit review. Token may be invalid or already used.');
      }
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.status === 400 || error.response?.status === 403 || error.response?.status === 409) {
        toast.error('Invalid or already used review link.');
      } else {
        toast.error('An error occurred while submitting your review.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <section className="cs_gray_bg cs_shape_animation_1" style={{ minHeight: '100vh', paddingTop: '120px', paddingBottom: '120px' }}>
        <Spacing lg="40" md="20" />
        <div className="container">
          <div className="text-center">
            <AnimatedSection animation="fadeUp">
              <SectionHeading title="Share Your Experience" subTitle="Product Review" />
            </AnimatedSection>
          </div>
          <Spacing lg="50" md="30" />
          
          <div className="row justify-content-center">
            <div className="col-lg-8 col-xl-6">
              <AnimatedSection animation="fadeUp" delay={0.1}>
                <div className="cs_white_bg p-4 p-md-5 cs_radius_15" style={{ boxShadow: '0 20px 40px rgba(0,0,0,0.05)', position: 'relative', overflow: 'hidden' }}>
                  
                  {/* Decorative top border */}
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '5px', background: '#0B5E4B' }}></div>

                  {!token ? (
                    <div className="text-center py-5">
                      <div className="cs_accent_bg text-white rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4" style={{ width: '80px', height: '80px' }}>
                        <span className="cs_fs_32">!</span>
                      </div>
                      <h3 className="cs_fs_24 cs_semibold mb-3">Invalid Link</h3>
                      <p className="cs_fs_16 text-muted mb-4">It seems your review link is invalid or missing the required token.</p>
                      <button onClick={() => navigate('/')} className="cs_btn cs_style_1">
                        Return to Home
                      </button>
                    </div>
                  ) : isSuccess ? (
                    <div className="text-center py-5">
                      <div className="cs_accent_bg text-white rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4" style={{ width: '80px', height: '80px', backgroundColor: '#2ECC71' }}>
                        <span className="cs_fs_32">✓</span>
                      </div>
                      <h3 className="cs_fs_24 cs_semibold mb-3">Thank You!</h3>
                      <p className="cs_fs_16 text-muted mb-4">Your review has been successfully submitted and will help others make an informed decision.</p>
                      <p className="cs_fs_14 text-muted">Redirecting you to the home page...</p>
                      <Spacing lg="20" md="10" />
                      <button onClick={() => navigate('/')} className="cs_btn cs_style_1">
                        Go Home Now
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <div className="form-group mb-4 text-start">
                        <label className="cs_fs_16 cs_semibold cs_primary_color mb-2">Your Name</label>
                        <input
                          type="text"
                          className="cs_form_field"
                          placeholder="Enter your name"
                          value={reviewerName}
                          onChange={(e) => setReviewerName(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '15px 20px',
                            border: '2px solid #dddddd',
                            borderRadius: '15px',
                            fontSize: '16px',
                            transition: 'all 0.3s ease',
                            background: '#f8f8f8',
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#0B5E4B';
                            e.target.style.background = '#ffffff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(11, 94, 75, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#dddddd';
                            e.target.style.background = '#f8f8f8';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </div>

                      <div className="text-center mb-4">
                        <p className="cs_fs_16 text-muted">Rate your experience with Ayumedix Wellness</p>
                        <div className="d-flex justify-content-center gap-2 mt-3">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              type="button"
                              key={star}
                              className="btn btn-link p-0 border-0 text-decoration-none"
                              style={{ outline: 'none' }}
                              onClick={() => setRating(star)}
                              onMouseEnter={() => setHover(star)}
                              onMouseLeave={() => setHover(rating)}
                            >
                              <Star
                                size={40}
                                fill={star <= (hover || rating) ? "#FFD700" : "none"}
                                color={star <= (hover || rating) ? "#FFD700" : "#ccc"}
                                style={{ transition: 'all 0.2s ease', transform: star <= (hover || rating) ? 'scale(1.1)' : 'scale(1)' }}
                              />
                            </button>
                          ))}
                        </div>
                        {rating > 0 && (
                          <div className="mt-2 text-muted cs_fs_14 cs_semibold">
                            {rating === 5 ? 'Excellent!' : rating === 4 ? 'Very Good' : rating === 3 ? 'Average' : rating === 2 ? 'Poor' : 'Terrible'}
                          </div>
                        )}
                      </div>

                      <Spacing lg="30" md="20" />

                      <div className="form-group mb-4">
                        <label className="cs_fs_16 cs_semibold cs_primary_color mb-2">Detailed Review</label>
                        <textarea
                          className="cs_form_field"
                          placeholder="Tell us about your experience, results, and what you liked the most..."
                          rows="5"
                          value={reviewText}
                          onChange={(e) => setReviewText(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '15px 20px',
                            border: '2px solid #dddddd',
                            borderRadius: '15px',
                            fontSize: '16px',
                            transition: 'all 0.3s ease',
                            background: '#f8f8f8',
                            resize: 'vertical',
                            minHeight: '120px'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#0B5E4B';
                            e.target.style.background = '#ffffff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(11, 94, 75, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#dddddd';
                            e.target.style.background = '#f8f8f8';
                            e.target.style.boxShadow = 'none';
                          }}
                        ></textarea>
                        <div className="d-flex justify-content-between mt-2">
                          <small className="text-muted">Minimum 10 characters.</small>
                          <small className={reviewText.length >= 10 ? "text-success" : "text-muted"}>
                            {reviewText.length} / 500
                          </small>
                        </div>
                      </div>

                      <Spacing lg="10" md="5" />

                      <button 
                        type="submit" 
                        className="cs_btn cs_style_1 w-100 d-flex justify-content-center align-items-center"
                        disabled={isSubmitting}
                        style={{ padding: '15px', borderRadius: '15px', fontSize: '18px' }}
                      >
                        {isSubmitting ? (
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        ) : null}
                        {isSubmitting ? 'Submitting...' : 'Submit Review'}
                      </button>
                    </form>
                  )}
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
